import type { TocSlide, TocTree } from '@app/models/toc.model';

export const LESSON_CONTENT_MODES = {
  GENERIC: 'generic',
  DOCUMENT_REFERENCE: 'document_reference',
  DOCUMENT_STRICT: 'document_strict',
} as const;

export type LessonContentMode =
  (typeof LESSON_CONTENT_MODES)[keyof typeof LESSON_CONTENT_MODES];

export function isDocumentStrictLesson(mode: string | null | undefined): boolean {
  return String(mode || '').toLowerCase() === LESSON_CONTENT_MODES.DOCUMENT_STRICT;
}

export function isQuizTocSlide(slide: Pick<TocSlide, 'contentType' | 'slideType'> | null | undefined): boolean {
  if (!slide) return false;
  const ct = String(slide.contentType || '').toUpperCase();
  if (ct === 'QUIZ') return true;
  const st = String(slide.slideType || '').toUpperCase();
  return st === 'QUIZ' || st === 'PQUIZ';
}

export function documentStrictSlideNeedsPageRef(
  lessonContentMode: string | null | undefined,
  slide: Pick<TocSlide, 'sourcePageRef' | 'contentType' | 'slideType'> | null | undefined,
): boolean {
  if (!isDocumentStrictLesson(lessonContentMode) || !slide || isQuizTocSlide(slide)) return false;
  return !String(slide.sourcePageRef ?? '').trim();
}

export function documentStrictMissingPageRefMessage(slideTitle?: string): string {
  const label = slideTitle?.trim() || 'This slide';
  return (
    `Document-specific lesson: "${label}" has no PDF pages set. ` +
    'Enter PDF page number(s) in the TOC (e.g. 12 or 12-14), save the TOC, then generate. ' +
    'Continue anyway without page refs?'
  );
}

export function listTocSlidesMissingSourcePageRef(
  lessonContentMode: string | null | undefined,
  toc: TocTree | null | undefined,
): { slideId: string; title: string }[] {
  if (!isDocumentStrictLesson(lessonContentMode) || !toc?.sections) return [];
  const missing: { slideId: string; title: string }[] = [];
  for (const sec of toc.sections) {
    for (const sl of sec.slides || []) {
      if (documentStrictSlideNeedsPageRef(lessonContentMode, sl)) {
        missing.push({
          slideId: sl.slideId,
          title: String(sl.title || '').trim() || sl.slideId,
        });
      }
    }
  }
  return missing;
}

/** @deprecated use documentStrictSlideNeedsPageRef */
export function primarySourceSlideNeedsPageRef(
  toc: Pick<TocTree, 'lessonContentMode'> | null | undefined,
  slide: Pick<TocSlide, 'sourcePageRef' | 'contentType' | 'slideType'> | null | undefined,
): boolean {
  return documentStrictSlideNeedsPageRef(toc?.lessonContentMode, slide);
}

/** @deprecated use documentStrictMissingPageRefMessage */
export function primarySourceMissingPageRefMessage(slideTitle?: string): string {
  return documentStrictMissingPageRefMessage(slideTitle);
}
